import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

import { decodeJWT } from "../../utils";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../redux/actions/loadingActions/loadingActions";

import { pinService } from "../../services/userServices";

const PinUi = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const checkIsTokenEmpty = !isEmpty(token);
  const roleName = decodeJWT(token)?.usertype;

  const [isShowPinPage, setIsShowPinPage] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [resp, setResp] = useState("");
  const [pin, setPin] = useState("");

  //! User Information
  const userInfo = useSelector((state) => state.userInfo);
  const [id] = useState(userInfo[0]?.id);
  const [firstName] = useState(userInfo[0]?.firstname);
  const [lastName] = useState(userInfo[0]?.lastname);
  const [phoneNumber] = useState(userInfo[0]?.nationalid);
  const [nationalNumber] = useState(userInfo[0]?.phonenumber);
  const [pinCode] = useState(userInfo[0]?.pin);

  //* Handle Check User Exist
  useEffect(() => {
    const basePath = "https://davoodigold.ir";

    const handlePageBlur = () => {
      sessionStorage.setItem("isLocked", "true");
      setIsLocked(true);
      setIsShowPinPage(false);
      setPin("");
    };

    const handlePageFocus = () => {
      const isSessionLocked = sessionStorage.getItem("isLocked");
      const currentUrl = window.location.href;

      if (isSessionLocked === "true" || !currentUrl.startsWith(basePath)) {
        setIsLocked(true);
        setIsShowPinPage(false);
        setPin("");
      }
    };

    window.addEventListener("blur", handlePageBlur);
    window.addEventListener("focus", handlePageFocus);

    return () => {
      window.removeEventListener("blur", handlePageBlur);
      window.removeEventListener("focus", handlePageFocus);
    };
  }, []);

  //* Handle Check User Login
  useEffect(() => {
    const basePath = "https://davoodigold.ir";
    const currentPath = basePath + location.pathname;

    const token = localStorage.getItem("token");
    const isLogin = !isEmpty(token);
    setIsLoggedIn(isLogin);

    const isPageLocked = sessionStorage.getItem("isLocked") === "true";
    if (isPageLocked) {
      setIsLocked(true);
      setIsShowPinPage(false);
      setPin("");
      return;
    }

    if (!isLogin) {
      setIsLocked(false);
      return;
    }

    if (!currentPath.startsWith(basePath)) {
      setIsLocked(true);
      setIsShowPinPage(false);
    } else {
      setIsLocked(false);
    }
  }, [location.pathname]);

  //* Handle Submit Pin
  const handleEnterPin = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      firstName,
      lastName,
      nationalNumber,
      phoneNumber,
      pin: String(pin),
    };

    try {
      dispatch(setLoadingTrueAction());
      await pinService(objData).then((response) => {
        setResp(response);
        if (response.data.status === 200) {
          setIsLocked(false);
          sessionStorage.setItem("activeSession", "true");
          toast.success("قفل باز شد!");
          setPin("");
        } else {
          toast.error(response?.data?.message);
          setPin("");
        }
      });
    } catch ({ response }) {
      if (response) {
        toast.error(response?.data?.message);
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  //* Handle Check Pin
  const addToPin = (value) => {
    if (pin.length < 4) {
      const updatedPin = pin + value;
      setPin(updatedPin);
    }
  };

  //* Handle Delete OR Cancel
  const handleDelete = () => {
    if (pin.length === 0) {
      setIsShowPinPage(false);
      sessionStorage.removeItem("activeSession");
    } else {
      setPin((prev) => prev.slice(0, -1));
    }
  };

  //* Handle Reset Pin
  const handleResetPin = () => {
    setIsLocked(false);
    sessionStorage.setItem("activeSession", "false");

    if (roleName === "ADMIN") {
      navigate("/Admin/Dashboard/Profile");
    } else {
      navigate("/Customer/Change-Password");
    }
  };

  //* Handle Check Exist User In Allow Pages
  useEffect(() => {
    const allowedPaths = [
      "/Admin/Dashboard/Profile",
      "/Customer/Change-Password",
      "/",
    ];

    const isSessionActive = sessionStorage.getItem("activeSession");
    if (
      allowedPaths.includes(location.pathname) ||
      isSessionActive === "true"
    ) {
      setIsLocked(false);
    } else {
      setIsLocked(true);
    }
  }, [location.pathname]);

  //* Animation variants for the welcome button
  const welcomeButtonVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.7,
        delayChildren: 1.2,
        staggerChildren: 0.8,
      },
    },
  };

  //* Animation variants for PIN entry
  const enterPinVariants = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
    pressed: { scale: 0.8, transition: { duration: 0.1 } },
  };

  return (
    <>
      {isLocked && checkIsTokenEmpty && (
        <motion.div
          dir="ltr"
          className="w-full h-full flex flex-col justify-center 
        items-center z-[9998] fixed backdrop-blur-xl"
          initial="hidden"
          animate="visible"
          variants={welcomeButtonVariants}
        >
          {/* Welcome Button */}
          {!isShowPinPage && (
            <motion.div
              className="w-full flex justify-center items-center"
              variants={{
                hidden: { y: "100vh" },
                visible: {
                  y: 0,
                  transition: {
                    type: "spring",
                    mass: 0.5,
                    damping: 8,
                  },
                },
              }}
            >
              <button
                onClick={() => setIsShowPinPage(true)}
                className="border-2 px-5 py-3 rounded-full text-slate-200"
              >
                ورود به سایت
              </button>
            </motion.div>
          )}

          {/* Enter PIN Section */}
          {isShowPinPage && (
            <div className="md:w-6/12 w-10/12 h-8/12 py-10 flex flex-col justify-center items-center">
              <form
                className="flex flex-col justify-center items-center space-y-4"
                onSubmit={(e) => e.preventDefault()}
              >
                {/* Text */}
                {!pinCode && (
                  <div>
                    <p className="text-slate-200 text-base">
                      لطفا پین خود را برای ثبت وارد کنید
                    </p>
                  </div>
                )}
                {/* PIN Display */}
                <div className="flex justify-center items-center w-11/12 h-full relative">
                  <div className="flex justify-center py-6 items-center w-full h-full space-x-4">
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className="w-4 h-4 rounded-full border-2 border-gray-500"
                        style={{
                          backgroundColor: pin[index]
                            ? "#cbd5e1"
                            : "transparent",
                        }}
                      />
                    ))}
                  </div>
                </div>
                {/* Number Pad */}
                <div className="grid grid-cols-3 gap-10">
                  {/* Numbers 1-9 */}
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <motion.button
                      key={num}
                      onClick={() => addToPin(num)}
                      className="w-16 h-16 bg-slate-300 rounded-full text-xl shadow-md"
                      whileTap={{ scale: 0.9 }}
                    >
                      {num}
                    </motion.button>
                  ))}
                  {/* Delete & Cancel */}
                  <span className="flex justify-center">
                    <motion.button
                      onClick={handleDelete}
                      className="flex justify-center items-center
                    w-12 h-full text-slate-200 text-base"
                      variants={enterPinVariants}
                      initial="hidden"
                      animate="visible"
                      whileTap="pressed"
                    >
                      {pin ? "حذف" : "لغو"}
                    </motion.button>
                  </span>
                  {/* Number 0 */}
                  <span className="flex justify-start">
                    <motion.button
                      onClick={() => addToPin(0)}
                      className="w-16 h-16 bg-slate-300 rounded-full text-xl shadow-md"
                      variants={enterPinVariants}
                      initial="hidden"
                      animate="visible"
                      whileTap="pressed"
                    >
                      0
                    </motion.button>
                  </span>
                  {/* Enter */}
                  <span className="flex justify-center">
                    <motion.button
                      onClick={handleEnterPin}
                      className="flex justify-center items-center
                      w-12 h-full text-slate-200 text-base"
                      variants={enterPinVariants}
                      initial="hidden"
                      animate="visible"
                      whileTap="pressed"
                    >
                      ورود
                    </motion.button>
                  </span>
                </div>
                {/* Reset Pin */}
                <div>
                  <span className="text-slate-200 text-base">
                    <button onClick={handleResetPin}>فراموشی پین</button>
                  </span>
                </div>
              </form>
            </div>
          )}
        </motion.div>
      )}
    </>
  );
};

export default PinUi;
